import { useState } from 'react';
import { money } from '../format';
import type { TeamConfigListOut } from '../types';
import { TeamSpendView } from './TeamSpendView';

interface TeamConfigListProps {
  teams: TeamConfigListOut;
  /** Set (or with null, remove) a team's budget cap; resolves to the server's warning. */
  onSetBudget?: (teamId: string, budgetUsd: number | null) => Promise<string | null>;
}

/**
 * Every team's configuration: the Entra group mapping that IS the authorisation model,
 * the workspace it maps to, and where guardrail-request notifications route. The Slack
 * webhook is masked server-side (host + last 4 chars), so this never shows the raw token.
 * Only the budget is editable here; the rest still comes from the upsert_team CLI.
 */
export function TeamConfigList({ teams, onSetBudget }: TeamConfigListProps) {
  const [notice, setNotice] = useState<string | null>(null);

  if (teams.items.length === 0) {
    return (
      <div className="empty">
        <p>No teams are registered.</p>
      </div>
    );
  }

  return (
    <div>
      {notice ? (
        <div className="callout callout-warning" role="status">
          {notice}
        </div>
      ) : null}
      <table className="keys">
        <caption className="sr-only">Team configuration</caption>
        <thead>
          <tr>
            <th scope="col">Team</th>
            <th scope="col">Workspace</th>
            <th scope="col">Member group</th>
            <th scope="col">Lead group</th>
            <th scope="col">Slack</th>
            <th scope="col">Budget</th>
          </tr>
        </thead>
        <tbody>
          {teams.items.map((team) => (
            <TeamConfigRow
              key={team.id}
              team={team}
              onSetBudget={
                onSetBudget
                  ? async (budget) => setNotice(await onSetBudget(team.id, budget))
                  : undefined
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TeamConfigRowProps {
  team: TeamConfigListOut['items'][number];
  onSetBudget: ((budgetUsd: number | null) => Promise<void>) | undefined;
}

function TeamConfigRow({ team, onSetBudget }: TeamConfigRowProps) {
  const [showSpend, setShowSpend] = useState(false);
  const [editing, setEditing] = useState(false);
  const [budget, setBudget] = useState(team.team_budget_usd !== null ? String(team.team_budget_usd) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    // An empty field removes the cap, the same as `--team-budget-usd` never set.
    const value = budget.trim() === '' ? null : Number(budget);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      setError('Budget must be a non-negative number, or empty for no budget');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSetBudget?.(value);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr className={team.is_active ? undefined : 'row-warning'}>
        <td>
          <div className="key-name">{team.name}</div>
          <div className="key-label muted" style={{ fontSize: '12px' }}>
            {team.slug}
            {!team.is_active && ' · archived'}
          </div>
          <button
            onClick={() => setShowSpend(!showSpend)}
            style={{ marginTop: '4px', fontSize: '12px' }}
          >
            {showSpend ? 'Hide' : 'View'} spend history
          </button>
        </td>
        <td>
          <code className="mono small">{team.openrouter_workspace_id}</code>
          {team.openrouter_workspace_slug && (
            <div className="muted" style={{ fontSize: '11px' }}>
              {team.openrouter_workspace_slug}
            </div>
          )}
        </td>
        <td>
          {team.entra_member_group_id ? (
            <code className="mono small">{team.entra_member_group_id}</code>
          ) : (
            <span className="muted">none</span>
          )}
        </td>
        <td>
          {team.entra_lead_group_id ? (
            <code className="mono small">{team.entra_lead_group_id}</code>
          ) : (
            <span className="muted">none</span>
          )}
        </td>
        <td>
          {team.slack_webhook_url ? (
            <code className="mono small">{team.slack_webhook_url}</code>
          ) : (
            <span className="muted">not configured</span>
          )}
        </td>
        <td className="numeric small">
          {editing ? (
            <div className="key-action-form">
              <input
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                disabled={saving}
                placeholder="No budget"
                aria-label={`Budget for ${team.name}`}
                step="0.01"
                min="0"
              />
              <button className="primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              {error ? <div className="action-error">{error}</div> : null}
              <p className="action-note">Leave empty for no budget cap.</p>
            </div>
          ) : (
            <>
              {team.team_budget_usd !== null ? (
                money(team.team_budget_usd)
              ) : (
                <span className="muted">none</span>
              )}
              {onSetBudget ? (
                <div>
                  <button
                    onClick={() => {
                      setBudget(team.team_budget_usd !== null ? String(team.team_budget_usd) : '');
                      setEditing(true);
                    }}
                    style={{ marginTop: '4px', fontSize: '12px' }}
                    aria-label={`Edit budget for ${team.name}`}
                  >
                    Edit
                  </button>
                </div>
              ) : null}
            </>
          )}
        </td>
      </tr>
      {showSpend && (
        <tr className="key-spend-row">
          <td colSpan={6}>
            <TeamSpendView teamId={team.id} teamName={team.name} />
          </td>
        </tr>
      )}
    </>
  );
}
